/* global trapFocus, removeTrapFocus */

class HeaderSearchModal extends HTMLElement {
  constructor() {
    super();
    this.detailsEl = this.querySelector('details');
    this.toggle = this.querySelector('summary');
    this.modal = this.querySelector('.header-search-modal');
    this.panel = this.querySelector('.header-search-modal__panel');
    this.closeBtn = this.querySelector('.js-close-modal');
    this.transitionMs = 300;

    this.onDetailsClick = this.onDetailsClick.bind(this);
    this.onModalClick = this.onModalClick.bind(this);
    this.onCloseClick = this.onCloseClick.bind(this);
    this.onEscape = this.onEscape.bind(this);
    this.onCloseTransitionEnd = this.onCloseTransitionEnd.bind(this);

    this.detailsEl.addEventListener('click', this.onDetailsClick);
    this.closeBtn.addEventListener('click', this.onCloseClick);
  }

  connectedCallback() {
    this.header = this.closest('store-header');
    this.modalHome = this.detailsEl;
    const speed = getComputedStyle(this.header || this).getPropertyValue('--header-transition-speed');
    if (speed) {
      const ms = parseFloat(speed) * (speed.trim().endsWith('ms') ? 1 : 1000);
      if (!Number.isNaN(ms)) this.transitionMs = ms;
    }
  }

  onDetailsClick(evt) {
    if (!this.detailsEl.open && this.toggle.contains(evt.target)) {
      evt.preventDefault();
      this.open();
    }
  }

  onModalClick(evt) {
    if (!this.detailsEl.open) return;
    if (evt.target === this.modal) {
      evt.preventDefault();
      this.close();
    }
  }

  onCloseClick(evt) {
    if (!this.detailsEl.open) return;
    evt.preventDefault();
    evt.stopPropagation();
    this.close();
  }

  onEscape(evt) {
    if (evt.key === 'Escape' && this.detailsEl.open) {
      evt.preventDefault();
      this.close();
    }
  }

  onCloseTransitionEnd(evt) {
    if (
      evt.propertyName === 'opacity'
      && evt.target === this.modal
      && this.detailsEl.classList.contains('is-closing')
    ) {
      this.finishClose();
    }
  }

  syncOverlaySize() {
    const modal = this.modal;
    const header = this.header;
    if (!modal || !header) return;

    const height = header.offsetHeight;

    modal.style.top = '0';
    modal.style.left = '0';
    modal.style.right = '0';
    modal.style.width = '100%';
    modal.style.height = `${height}px`;
    header.style.setProperty('--header-search-overlay-height', `${height}px`);
    header.style.minHeight = `${height}px`;
  }

  portalModal(open) {
    const modal = this.modal;
    const header = this.header;
    if (!modal || !header || !this.modalHome) return;

    if (open) {
      modal.removeAttribute('hidden');
      if (modal.parentElement !== header) {
        header.appendChild(modal);
      }
      this.syncOverlaySize();
      modal.addEventListener('click', this.onModalClick);
      modal.addEventListener('transitionend', this.onCloseTransitionEnd);
      this.resizeHandler = this.resizeHandler || (() => this.syncOverlaySize());
      window.addEventListener('resize', this.resizeHandler);
      window.addEventListener('scroll', this.resizeHandler, { passive: true });
    } else if (modal.parentElement === header) {
      modal.setAttribute('hidden', '');
      modal.removeEventListener('click', this.onModalClick);
      modal.removeEventListener('transitionend', this.onCloseTransitionEnd);
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
        window.removeEventListener('scroll', this.resizeHandler);
      }
      modal.style.top = '';
      modal.style.left = '';
      modal.style.right = '';
      modal.style.width = '';
      modal.style.height = '';
      header.style.removeProperty('--header-search-overlay-height');
      header.style.minHeight = '';
      this.modalHome.appendChild(modal);
    }
  }

  setOpenState(isOpen) {
    this.header?.classList.toggle('header-search-is-open', isOpen);
  }

  open() {
    this.detailsEl.open = true;
    this.syncOverlaySize();
    this.portalModal(true);

    this.modal.classList.remove('is-closing');
    this.detailsEl.classList.remove('is-closing');

    document.addEventListener('keydown', this.onEscape);

    // Apply open state before trapping focus — modal/input stay visibility:hidden until then.
    requestAnimationFrame(() => {
      this.syncOverlaySize();
      this.setOpenState(true);

      requestAnimationFrame(() => {
        const input = this.querySelector('.js-search-input');
        trapFocus(this.modal, input || this.panel);
      });
    });
  }

  restoreToggleFocus() {
    if (!this.toggle) return;

    requestAnimationFrame(() => {
      this.toggle.focus();
    });
  }

  close() {
    if (!this.detailsEl.open || this.detailsEl.classList.contains('is-closing')) return;

    document.removeEventListener('keydown', this.onEscape);

    this.setOpenState(false);
    this.modal.classList.add('is-closing');
    this.detailsEl.classList.add('is-closing');

    removeTrapFocus();
    this.restoreToggleFocus();

    this.closeFallbackTimer = setTimeout(() => {
      if (this.detailsEl.classList.contains('is-closing')) {
        this.finishClose();
      }
    }, this.transitionMs + 50);

    if (window.setHeaderHeight) {
      setTimeout(window.setHeaderHeight, this.transitionMs);
    }
  }

  finishClose() {
    if (!this.detailsEl.classList.contains('is-closing')) return;

    clearTimeout(this.closeFallbackTimer);

    this.detailsEl.classList.remove('is-closing');
    this.modal?.classList.remove('is-closing');
    this.detailsEl.open = false;
    this.portalModal(false);
    this.setOpenState(false);
    this.restoreToggleFocus();
  }
}

if (!customElements.get('header-search-modal')) {
  customElements.define('header-search-modal', HeaderSearchModal);
}
